"use client";

import { useEffect, useMemo, useState } from "react";
import { Download, Smartphone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type DeferredInstallPrompt = {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function PublicInstallPrompt() {
  const { toast } = useToast();
  const [installPrompt, setInstallPrompt] = useState<DeferredInstallPrompt | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const isiOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iphone|ipad|ipod/i.test(ua);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const standaloneMatch = window.matchMedia?.("(display-mode: standalone)")?.matches;
    const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
    setIsInstalled(Boolean(standaloneMatch || iosStandalone));

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as unknown as DeferredInstallPrompt);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice.catch(() => undefined);
      setInstallPrompt(null);
      return;
    }

    if (isiOS) {
      toast({
        title: "Add to Home Screen on iPhone",
        description: "In Safari, open Share and choose Add to Home Screen.",
      });
      return;
    }

    toast({
      title: "Install from browser menu",
      description: "Use your browser menu and choose Install app/Add to Home Screen.",
    });
  };

  if (isInstalled) {
    return null;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Smartphone className="h-4 w-4" /> Add Zamzi to your Home Screen
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Install for faster loading and one-tap access. Notifications are optional.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={handleInstall}>
          <Download className="mr-2 h-4 w-4" /> Add to Home Screen
        </Button>
      </div>
    </div>
  );
}
